import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { joinWaitlist } from "@/lib/api";

export default function WaitlistForm({ source = "shop_coming_soon", compact = false }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      await joinWaitlist(email.trim().toLowerCase(), source);
      setDone(true);
      setEmail("");
      toast.success("You're on the list!", {
        description: "We'll notify you the moment we launch.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className={`flex items-center gap-3 rounded-full border border-[#28C0D8]/40 bg-[#28C0D8]/10 px-5 py-3 text-sm text-white ${
          compact ? "" : "md:text-base"
        }`}
        data-testid="waitlist-success"
      >
        <CheckCircle2 className="h-5 w-5 text-[#28C0D8]" />
        You&apos;re on the waitlist. We&apos;ll be in touch.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`w-full ${compact ? "" : "max-w-xl"}`}
      data-testid="waitlist-form"
    >
      <div
        className={`flex flex-col sm:flex-row sm:items-stretch rounded-2xl sm:rounded-full border border-white/10 bg-[#0A0B0E]/60 backdrop-blur focus-within:border-[#28C0D8] focus-within:shadow-[0_0_0_4px_rgba(40,192,216,0.15)] transition gap-1.5 sm:gap-0 ${
          compact ? "p-1" : "p-1.5 sm:p-1 md:p-1.5"
        }`}
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email for launch updates"
          aria-label="Email address"
          className={`min-w-0 flex-1 bg-transparent px-4 text-white placeholder-white/40 outline-none ${
            compact ? "py-2 text-sm" : "py-2.5 text-sm md:py-3 md:text-base"
          }`}
          data-testid="waitlist-email-input"
        />
        <button
          type="submit"
          disabled={loading}
          className={`btn-teal-glow inline-flex items-center justify-center gap-2 rounded-full bg-[#28C0D8] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC] disabled:opacity-60 whitespace-nowrap ${
            compact ? "px-4 py-2 text-sm" : "px-4 py-2.5 text-sm md:px-6 md:py-3 md:text-base"
          }`}
          data-testid="waitlist-submit-btn"
        >
          {loading ? "Joining…" : "Notify Me"}
          {!loading && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
      {!compact && (
        <p className="mt-3 text-xs text-white/50 leading-relaxed">
          By submitting, you agree to receive launch updates from MD Supply
          Wellness. You must be 21+.
        </p>
      )}
    </form>
  );
}
